import { Undef } from 'types/UtilityTypes';

const EPSILON = 0.0001;

// "Skyline" refers to the top contour formed by already placed pallets in the trailer.
// Each new placement is evaluated against that contour to find the best fit,
// minimizing packed depth (linear feet) while respecting trailer width constraints.

export interface SkylinePackingUnit {
  widthInches: number;
  lengthInches: number;
  canRotate: boolean;
}

interface SkylineSegment {
  x: number;
  width: number;
  height: number;
}

interface SkylinePlacementCandidate {
  unitWidth: number;
  unitLength: number;
  x: number;
  resultingMaxHeight: number;
  horizontalWaste: number;
  completesRow: boolean;
  fillsFutureGap: boolean;
  baseHeight: number;
  segmentSpanCount: number;
  exactGapFit: boolean;
}

export interface PackingFootprint {
  packedLengthInches: number;
  packedWidthInches: number;
  maxShelfDepthInches?: number;
}

export interface SkylinePlacement {
  xInches: number;
  zInches: number;
  widthInches: number;
  lengthInches: number;
}

export interface PackingFootprintWithPlacements extends PackingFootprint {
  placements: SkylinePlacement[];
}

interface SkylinePlacementOptions {
  reserveOverflowRows?: boolean;
}

interface SkylinePackingOptions extends SkylinePlacementOptions {
  collectPlacements?: boolean;
}

function selectBetterSkylineCandidate(
  candidate: SkylinePlacementCandidate,
  best: SkylinePlacementCandidate
): boolean {
  if (Number(candidate.completesRow) > Number(best.completesRow)) {
    return true;
  }
  if (Number(candidate.completesRow) < Number(best.completesRow)) {
    return false;
  }
  if (Number(candidate.fillsFutureGap) > Number(best.fillsFutureGap)) {
    return true;
  }
  if (Number(candidate.fillsFutureGap) < Number(best.fillsFutureGap)) {
    return false;
  }
  if (candidate.resultingMaxHeight < best.resultingMaxHeight - EPSILON) {
    return true;
  }
  if (candidate.resultingMaxHeight > best.resultingMaxHeight + EPSILON) {
    return false;
  }
  if (candidate.horizontalWaste < best.horizontalWaste - EPSILON) {
    return true;
  }
  if (candidate.horizontalWaste > best.horizontalWaste + EPSILON) {
    return false;
  }
  if (Number(candidate.exactGapFit) > Number(best.exactGapFit)) {
    return true;
  }
  if (Number(candidate.exactGapFit) < Number(best.exactGapFit)) {
    return false;
  }
  if (candidate.segmentSpanCount < best.segmentSpanCount) {
    return true;
  }
  if (candidate.segmentSpanCount > best.segmentSpanCount) {
    return false;
  }
  if (candidate.baseHeight < best.baseHeight - EPSILON) {
    return true;
  }
  if (candidate.baseHeight > best.baseHeight + EPSILON) {
    return false;
  }
  return candidate.x < best.x;
}

function pickBestSkylinePlacementAmong(
  candidates: SkylinePlacementCandidate[]
): SkylinePlacementCandidate | null {
  if (!candidates.length) {
    return null;
  }
  const minH = Math.min(...candidates.map((c) => c.resultingMaxHeight));
  const atMinDepth = candidates.filter(
    (c) => c.resultingMaxHeight <= minH + EPSILON
  );
  let best = atMinDepth[0];
  for (let i = 1; i < atMinDepth.length; i += 1) {
    if (selectBetterSkylineCandidate(atMinDepth[i], best)) {
      best = atMinDepth[i];
    }
  }
  return best;
}

function canFutureUnitsFillWidth(
  remainingWidth: number,
  remainingUnits: SkylinePackingUnit[]
): boolean {
  if (remainingWidth <= EPSILON) {
    return false;
  }
  const searchUnits = remainingUnits.slice(0, 10);
  for (const unit of searchUnits) {
    if (Math.abs(unit.widthInches - remainingWidth) <= EPSILON) {
      return true;
    }
    if (
      unit.canRotate &&
      Math.abs(unit.lengthInches - remainingWidth) <= EPSILON
    ) {
      return true;
    }
  }
  for (let i = 0; i < searchUnits.length; i += 1) {
    const unitA = searchUnits[i];
    const unitAWidths = [unitA.widthInches];
    if (
      unitA.canRotate &&
      Math.abs(unitA.lengthInches - unitA.widthInches) > EPSILON
    ) {
      unitAWidths.push(unitA.lengthInches);
    }
    for (let j = i + 1; j < searchUnits.length; j += 1) {
      const unitB = searchUnits[j];
      const unitBWidths = [unitB.widthInches];
      if (
        unitB.canRotate &&
        Math.abs(unitB.lengthInches - unitB.widthInches) > EPSILON
      ) {
        unitBWidths.push(unitB.lengthInches);
      }
      for (const widthA of unitAWidths) {
        for (const widthB of unitBWidths) {
          if (Math.abs(widthA + widthB - remainingWidth) <= EPSILON) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

function collectSkylineBreakpoints(
  skyline: SkylineSegment[],
  trailerWidthInches: number
): number[] {
  const xBreakpoints = new Set<number>();
  xBreakpoints.add(0);
  xBreakpoints.add(trailerWidthInches);
  for (const segment of skyline) {
    xBreakpoints.add(segment.x);
    xBreakpoints.add(segment.x + segment.width);
  }
  return [...xBreakpoints]
    .filter((x) => x >= -EPSILON && x <= trailerWidthInches + EPSILON)
    .sort((a, b) => a - b);
}

function collectSkylinePlacementXCandidates(
  skyline: SkylineSegment[],
  trailerWidthInches: number,
  unitWidth: number
): number[] {
  const boundaries = collectSkylineBreakpoints(skyline, trailerWidthInches);
  return boundaries
    .filter(
      (x) => x >= -EPSILON && x + unitWidth <= trailerWidthInches + EPSILON
    )
    .sort((a, b) => a - b);
}

interface IntervalPlacementMetrics {
  valid: boolean;
  maxBaseHeight: number;
  segmentSpanCount: number;
  horizontalWaste: number;
  exactGapFit: boolean;
}

function computeIntervalPlacementMetrics(
  skyline: SkylineSegment[],
  placementX: number,
  unitWidth: number,
  trailerWidthInches: number
): IntervalPlacementMetrics {
  const xEnd = placementX + unitWidth;
  if (placementX < -EPSILON || xEnd - trailerWidthInches > EPSILON) {
    return {
      valid: false,
      maxBaseHeight: 0,
      segmentSpanCount: 0,
      horizontalWaste: 0,
      exactGapFit: false,
    };
  }
  let overlapSum = 0;
  let maxBaseHeight = 0;
  let segmentSpanCount = 0;
  let coveredWidthFullSegments = 0;
  for (const segment of skyline) {
    const segmentRight = segment.x + segment.width;
    const lo = Math.max(placementX, segment.x);
    const hi = Math.min(xEnd, segmentRight);
    if (hi - lo <= EPSILON) {
      continue;
    }
    overlapSum += hi - lo;
    maxBaseHeight = Math.max(maxBaseHeight, segment.height);
    segmentSpanCount += 1;
    coveredWidthFullSegments += segment.width;
  }
  const valid = Math.abs(overlapSum - unitWidth) <= EPSILON;
  const horizontalWaste = coveredWidthFullSegments - unitWidth;
  return {
    valid,
    maxBaseHeight,
    segmentSpanCount,
    horizontalWaste,
    exactGapFit: Math.abs(horizontalWaste) <= EPSILON,
  };
}

function applySkylinePlacement(
  skyline: SkylineSegment[],
  placement: SkylinePlacementCandidate
): SkylineSegment[] {
  const placedStart = placement.x;
  const placedEnd = placement.x + placement.unitWidth;
  const placedHeight = placement.baseHeight + placement.unitLength;
  const next: SkylineSegment[] = [];
  for (const segment of skyline) {
    const segmentStart = segment.x;
    const segmentEnd = segment.x + segment.width;
    if (
      segmentEnd <= placedStart + EPSILON ||
      segmentStart >= placedEnd - EPSILON
    ) {
      next.push(segment);
      continue;
    }
    if (segmentStart < placedStart - EPSILON) {
      next.push({
        x: segmentStart,
        width: placedStart - segmentStart,
        height: segment.height,
      });
    }
    if (segmentEnd > placedEnd + EPSILON) {
      next.push({
        x: placedEnd,
        width: segmentEnd - placedEnd,
        height: segment.height,
      });
    }
  }
  next.push({
    x: placedStart,
    width: placement.unitWidth,
    height: placedHeight,
  });
  return mergeSkylineSegments(next);
}

function evaluateSkylinePlacement(
  skyline: SkylineSegment[],
  unitWidth: number,
  unitLength: number,
  currentMaxHeight: number,
  trailerWidthInches: number,
  remainingUnits: SkylinePackingUnit[]
): SkylinePlacementCandidate | null {
  const placementXs = collectSkylinePlacementXCandidates(
    skyline,
    trailerWidthInches,
    unitWidth
  );
  const candidates: SkylinePlacementCandidate[] = [];
  for (const x of placementXs) {
    const metrics = computeIntervalPlacementMetrics(
      skyline,
      x,
      unitWidth,
      trailerWidthInches
    );
    if (!metrics.valid) {
      continue;
    }
    const candidate: SkylinePlacementCandidate = {
      unitWidth,
      unitLength,
      x,
      resultingMaxHeight: Math.max(
        currentMaxHeight,
        metrics.maxBaseHeight + unitLength
      ),
      horizontalWaste: metrics.horizontalWaste,
      completesRow: Math.abs(x + unitWidth - trailerWidthInches) <= EPSILON,
      fillsFutureGap: canFutureUnitsFillWidth(
        trailerWidthInches - (x + unitWidth),
        remainingUnits
      ),
      baseHeight: metrics.maxBaseHeight,
      segmentSpanCount: metrics.segmentSpanCount,
      exactGapFit: metrics.exactGapFit,
    };
    candidates.push(candidate);
  }
  return pickBestSkylinePlacementAmong(candidates);
}

function mergeSkylineSegments(segments: SkylineSegment[]): SkylineSegment[] {
  if (!segments.length) {
    return [];
  }
  const sorted = [...segments].sort((a, b) => a.x - b.x);
  const merged: SkylineSegment[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const previous = merged[merged.length - 1];
    const current = sorted[i];
    if (
      Math.abs(previous.height - current.height) <= EPSILON &&
      Math.abs(previous.x + previous.width - current.x) <= EPSILON
    ) {
      previous.width += current.width;
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

function packUnitsWithSkylineInternal(
  units: SkylinePackingUnit[],
  trailerWidthInches: number,
  options: SkylinePackingOptions = {}
): PackingFootprint & { placements?: Undef<SkylinePlacement[]> } {
  if (!units.length) {
    return {
      packedLengthInches: 0,
      packedWidthInches: 0,
      maxShelfDepthInches: 0,
      placements: options.collectPlacements ? [] : undefined,
    };
  }

  let skyline: SkylineSegment[] = [
    { x: 0, width: trailerWidthInches, height: 0 },
  ];
  let maxHeight = 0;
  let maxShelfDepthInches = 0;

  const placements: Undef<SkylinePlacement[]> = options.collectPlacements
    ? []
    : undefined;

  for (let unitIndex = 0; unitIndex < units.length; unitIndex += 1) {
    const unit = units[unitIndex];
    const remainingUnits = units.slice(unitIndex + 1);
    const orientations: Array<[number, number]> = [
      [unit.widthInches, unit.lengthInches],
    ];
    if (
      unit.canRotate &&
      Math.abs(unit.widthInches - unit.lengthInches) > EPSILON
    ) {
      orientations.push([unit.lengthInches, unit.widthInches]);
    }
    const orientationCandidates: SkylinePlacementCandidate[] = [];
    for (const [width, length] of orientations) {
      const candidate = evaluateSkylinePlacement(
        skyline,
        width,
        length,
        maxHeight,
        trailerWidthInches,
        remainingUnits
      );
      if (candidate) {
        orientationCandidates.push(candidate);
      }
    }
    const best = pickBestSkylinePlacementAmong(orientationCandidates);
    if (!best) {
      const overflowX = Math.max(
        0,
        (trailerWidthInches - unit.widthInches) / 2
      );

      maxShelfDepthInches = Math.max(maxShelfDepthInches, unit.lengthInches);

      placements?.push({
        xInches: overflowX,
        zInches: maxHeight,
        widthInches: unit.widthInches,
        lengthInches: unit.lengthInches,
      });

      maxHeight += unit.lengthInches;

      if (options.reserveOverflowRows) {
        skyline = [
          {
            x: 0,
            width: trailerWidthInches,
            height: maxHeight,
          },
        ];
      }

      continue;
    }

    placements?.push({
      xInches: best.x,
      zInches: best.baseHeight,
      widthInches: best.unitWidth,
      lengthInches: best.unitLength,
    });

    skyline = applySkylinePlacement(skyline, best);

    maxShelfDepthInches = Math.max(maxShelfDepthInches, best.unitLength);

    maxHeight = Math.max(maxHeight, best.resultingMaxHeight);
  }

  return {
    packedLengthInches: maxHeight,
    packedWidthInches: skyline.reduce((maxEnd, segment) => {
      if (segment.height <= EPSILON) {
        return maxEnd;
      }
      return Math.max(maxEnd, segment.x + segment.width);
    }, 0),
    maxShelfDepthInches,
    placements,
  };
}

// Packs pallet footprints using a skyline profile (x-span -> contour depth).
// "Skyline" in side-loading PRs refers to this packing algorithm.
function packUnitsWithSkyline(
  units: SkylinePackingUnit[],
  trailerWidthInches: number
): PackingFootprint {
  return packUnitsWithSkylineInternal(units, trailerWidthInches);
}

export function packUnitsWithSkylinePlacements(
  units: SkylinePackingUnit[],
  trailerWidthInches: number,
  options: SkylinePlacementOptions = {}
): PackingFootprintWithPlacements {
  const footprint = packUnitsWithSkylineInternal(units, trailerWidthInches, {
    ...options,
    collectPlacements: true,
  });

  return {
    packedLengthInches: footprint.packedLengthInches,
    packedWidthInches: footprint.packedWidthInches,
    maxShelfDepthInches: footprint.maxShelfDepthInches,
    placements: footprint.placements ?? [],
  };
}

// Skyline-based 2D trailer packing.
// Exported name is preserved for compatibility with existing callers.
export function packUnitsWithSkylineAndLocalImprovements(
  sortedUnits: SkylinePackingUnit[],
  trailerWidthInches: number
): PackingFootprint {
  return packUnitsWithSkyline(sortedUnits, trailerWidthInches);
}
